import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  X, 
  Check, 
  Brain,
  Settings,
  BookOpen,
  Sparkles,
  Lightbulb,
  Target,
  Link2,
  Lock
} from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import { getDoc, doc } from 'firebase/firestore';
import BackButton from '../components/BackButton';
import StartSidebar from '../components/StartSidebar';

const StartGPTs = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for plans popup
  const [showPlansPopup, setShowPlansPopup] = useState(false);

  // State for customize popup
  const [showCustomizePopup, setShowCustomizePopup] = useState(false);

  // State for memory popup
  const [showMemoryPopup, setShowMemoryPopup] = useState(false);

  // State for connectors popup
  const [showConnectorsPopup, setShowConnectorsPopup] = useState(false);

  // State for prompt popup
  const [showPromptPopup, setShowPromptPopup] = useState(false);

  // State for features popup
  const [showFeaturesPopup, setShowFeaturesPopup] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);

  // State for fetched data
  const [whatsNewItems, setWhatsNewItems] = useState([]);
  const [gptLibraryItems, setGptLibraryItems] = useState([]);
  const [featuresItems, setFeaturesItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockedGPTs, setUnlockedGPTs] = useState(null); // null means not set, array means explicitly set

  // Fetch data from Firebase
  useEffect(() => {
    if (currentUser) {
      fetchAllData();
      fetchUnlockedGPTs();
    }
  }, [currentUser]);

  const fetchUnlockedGPTs = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let accountId = null;
      let unlockedGPTsList = null;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        accountId = userData.accountId;
        
        if (accountId) {
          try {
            const accountData = await getAccountById(accountId);
            if (accountData && accountData.unlockedGPTs !== undefined) {
              unlockedGPTsList = Array.isArray(accountData.unlockedGPTs) ? accountData.unlockedGPTs : [];
            }
          } catch (error) {
            console.error('Error fetching account data:', error);
          }
        }
      }

      setUnlockedGPTs(unlockedGPTsList);
    } catch (error) {
      console.error('Error fetching unlocked GPTs:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch What's New items
      const whatsNewRef = collection(db, 'whatsNew');
      const whatsNewQuery = query(whatsNewRef, orderBy('createdAt', 'desc'));
      const whatsNewSnapshot = await getDocs(whatsNewQuery);
      const whatsNew = whatsNewSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setWhatsNewItems(whatsNew);
      
      // Fetch GPTs
      const gptsRef = collection(db, 'gpts');
      const gptsQuery = query(gptsRef, orderBy('createdAt', 'desc'));
      const gptsSnapshot = await getDocs(gptsQuery);
      const gpts = gptsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setGptLibraryItems(gpts);
      
      // Fetch Features
      const featuresRef = collection(db, 'features');
      const featuresQuery = query(featuresRef, orderBy('createdAt', 'desc'));
      const featuresSnapshot = await getDocs(featuresQuery);
      const features = featuresSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setFeaturesItems(features);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className={`p-6`}>
        <div className="flex justify-between items-center mb-8">
          <BackButton onClick={() => navigate('/start/technology')} />
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex-1 text-center`}>ChatGPT</h1>
          <div className="w-10"></div> {/* Spacer to center the title */}
        </div>
        
        {/* Quick Action Cards */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div 
            onClick={() => setShowPromptPopup(true)}
            className={`${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'} rounded-lg shadow-md p-4 cursor-pointer transition-colors`}
          >
            <div className="flex flex-col items-center text-center">
              <MessageSquare size={24} className="text-white mb-2" />
              <span className="text-white font-medium text-sm">Prompt</span>
            </div>
          </div>
          
          <div 
            onClick={() => setShowPlansPopup(true)}
            className={`${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} rounded-lg shadow-md p-4 cursor-pointer transition-colors`}
          >
            <div className="flex flex-col items-center text-center">
              <Target size={24} className="text-white mb-2" />
              <span className="text-white font-medium text-sm">Plans</span>
            </div>
          </div>
          
          <div 
            onClick={() => setShowCustomizePopup(true)}
            className={`${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} rounded-lg shadow-md p-4 cursor-pointer transition-colors`}
          >
            <div className="flex flex-col items-center text-center">
              <Settings size={24} className="text-white mb-2" />
              <span className="text-white font-medium text-sm">Customize</span>
            </div>
          </div>
          
          <div 
            onClick={() => setShowMemoryPopup(true)}
            className={`${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} rounded-lg shadow-md p-4 cursor-pointer transition-colors`}
          >
            <div className="flex flex-col items-center text-center">
              <Brain size={24} className="text-white mb-2" />
              <span className="text-white font-medium text-sm">Memory</span>
            </div>
          </div>
          
          <div 
            onClick={() => setShowConnectorsPopup(true)}
            className={`${darkMode ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'} rounded-lg shadow-md p-4 cursor-pointer transition-colors`}
          >
            <div className="flex flex-col items-center text-center">
              <Link2 size={24} className="text-white mb-2" />
              <span className="text-white font-medium text-sm">Connectors</span>
            </div>
          </div>
        </div>
        
        {/* What's New and GPT Library Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* What's New Section */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-center gap-2 mb-6">
              <Sparkles size={24} className="text-yellow-500" />
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>What's New</h2>
            </div>
            <div className="space-y-4">
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading...
                </div>
              ) : whatsNewItems.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No updates available
                </div>
              ) : (
                whatsNewItems.map((item) => (
                  <div 
                    key={item.id}
                    className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg shadow-sm p-4 border`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <Sparkles size={20} className="text-yellow-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                        <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* GPT Library Section */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-center gap-2 mb-6">
              <BookOpen size={24} className="text-blue-500" />
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>GPT Library</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading...
                </div>
              ) : gptLibraryItems.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No GPTs available
                </div>
              ) : (
                gptLibraryItems.map((gpt) => {
                  // Check if this GPT is unlocked
                  const isUnlocked = unlockedGPTs === null || (Array.isArray(unlockedGPTs) && unlockedGPTs.includes(gpt.id));
                  
                  return (
                    <div 
                      key={gpt.id}
                      onClick={() => {
                        if (isUnlocked) {
                          navigate(`/start/technology/chatgpt/${gpt.id}`);
                        }
                      }}
                      className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg shadow-sm p-4 border relative transition-colors ${
                        isUnlocked
                          ? 'hover:bg-gray-600 dark:hover:bg-gray-600 cursor-pointer'
                          : 'opacity-60 cursor-not-allowed'
                      }`}
                    >
                      {!isUnlocked && (
                        <div className="absolute top-3 right-3">
                          <Lock className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                      )}
                      <div className="flex flex-col items-center text-center">
                        <div className="flex items-center justify-center mb-3">
                          {gpt.imageUrl ? (
                            <img 
                              src={gpt.imageUrl} 
                              alt={gpt.title}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <Brain size={32} className="text-blue-500" />
                          )}
                        </div>
                        
                        <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{gpt.title}</h3>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <Lightbulb size={24} className="text-orange-500" />
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Features</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading...
              </div>
            ) : featuresItems.length === 0 ? (
              <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                No features available
              </div>
            ) : (
              featuresItems.map((feature) => (
                <div 
                  key={feature.id}
                  onClick={() => {
                    setSelectedFeature(feature);
                    setShowFeaturesPopup(true);
                  }}
                  className={`${darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} rounded-lg shadow-sm p-4 border cursor-pointer transition-colors`}
                >
                  <div className="flex items-center justify-center mb-3">
                    {feature.imageUrl ? (
                      <img 
                        src={feature.imageUrl} 
                        alt={feature.title}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <Lightbulb size={24} className="text-orange-500" />
                    )}
                  </div>
                  
                  <h3 className={`font-medium text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{feature.title}</h3>
                </div>
              ))
            )}
          </div>
        </div>

        {/* All popups remain the same as GPTs.js */}
        {/* Plans Popup */}
        {showPlansPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-10"></div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>ChatGPT Plans</h2>
                  <button 
                    onClick={() => setShowPlansPopup(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X size={24} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </button>
                </div>

                {/* Plan Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Free Plan */}
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <div className="text-center mb-4">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Free</h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Basic access</p>
                    </div>
                    <ul className={`text-sm space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li>• Basic ChatGPT access</li>
                      <li>• Limited features</li>
                      <li>• No GPT creation</li>
                    </ul>
                  </div>

                  {/* Plus Plan */}
                  <div className={`${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4`}>
                    <div className="text-center mb-4">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Plus</h3>
                      <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>$20/month</p>
                    </div>
                    <ul className={`text-sm space-y-2 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                      <li>• Enhanced ChatGPT access</li>
                      <li>• Priority support</li>
                      <li>• GPT creation</li>
                      <li>• Sora</li>
                    </ul>
                  </div>

                  {/* Team Plan */}
                  <div className={`${darkMode ? 'bg-purple-900/20 border-purple-500' : 'bg-purple-50 border-purple-200'} rounded-lg border p-4`}>
                    <div className="text-center mb-4">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>Team</h3>
                      <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>$25/month per user</p>
                    </div>
                    <ul className={`text-sm space-y-2 ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                      <li>• All Plus features</li>
                      <li>• GPT creation & sharing</li>
                      <li>• Team workspace</li>
                      <li>• Collaborative tools</li>
                    </ul>
                  </div>

                  {/* Pro Plan */}
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <div className="text-center mb-4">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Pro</h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>$200/month</p>
                    </div>
                    <ul className={`text-sm space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li>• All Team features</li>
                      <li>• Operator</li>
                      <li>• Priority access</li>
                      <li>• Enterprise support</li>
                    </ul>
                  </div>
                </div>

                {/* Recommendation */}
                <div className={`${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <Target size={20} className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Recommended Plans</h4>
                      <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        To access all GPT features and create custom GPTs for your business, we recommend either a <strong>Plus account ($20/month)</strong> or a <strong>Team account ($25/month per user)</strong>. The Team plan is the same as Plus but includes a collaborative workspace for your team.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end">
                  <button 
                    onClick={() => setShowPlansPopup(false)}
                    className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customize Popup */}
        {showCustomizePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-10"></div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Customize ChatGPT</h2>
                  <button 
                    onClick={() => setShowCustomizePopup(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X size={24} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </button>
                </div>

                {/* Instructions */}
                <div className={`${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <Settings size={20} className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Instructions</h4>
                      <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        Copy these settings and enter them into the "Customize ChatGPT" section in your ChatGPT account. This will help ChatGPT understand your business context and provide more relevant assistance.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Customization Sections */}
                <div className="space-y-6">
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>What should ChatGPT call you?</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>[Your First Name]</p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>What do you do?</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Owner of [Company Name] located at [City, State]</p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>What traits should ChatGPT have?</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Professional, business-focused, detail-oriented, strategic thinker, results-driven, and knowledgeable about business operations, marketing, sales, and growth strategies.
                      </p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Anything else ChatGPT should know about you?</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        I'm focused on growing my business and need practical, actionable advice. I value efficiency, clear communication, and solutions that can be implemented quickly. I'm looking for strategic insights and tactical recommendations to improve my business operations, marketing, sales, and overall growth.
                      </p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Capabilities to Enable</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-green-500" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Web Search</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-green-500" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Code</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-green-500" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Canvas</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-green-500" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Advanced Voice</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button 
                    onClick={() => setShowCustomizePopup(false)}
                    className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Memory Popup */}
        {showMemoryPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-10"></div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>ChatGPT Memory</h2>
                  <button 
                    onClick={() => setShowMemoryPopup(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X size={24} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </button>
                </div>

                <div className={`${darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-200'} rounded-lg border p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <Brain size={20} className={`${darkMode ? 'text-green-400' : 'text-green-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Enable Memory Features</h4>
                      <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                        To enable ChatGPT's memory capabilities, you need to turn on specific settings in your ChatGPT account.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Step 1: Access Settings</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Go to your ChatGPT account settings by clicking on your profile picture in the bottom left corner and selecting "Settings"
                      </p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Step 2: Enable Memory Features</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        In the Personalization section, turn on both:
                      </p>
                      <ul className={`text-sm mt-2 space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li>• <strong>Reference saved memories</strong></li>
                        <li>• <strong>Reference chat history</strong></li>
                      </ul>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Step 3: Manage Memories</h3>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        If you ever want to delete a chat from memory, click the <strong>Manage</strong> button next to "Manage memories" in your settings.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4 mt-6`}>
                  <div className="flex items-start gap-3">
                    <Brain size={20} className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Benefits of Memory</h4>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        <li>• ChatGPT remembers your preferences and past conversations</li>
                        <li>• More personalized and contextual responses</li>
                        <li>• Better understanding of your business needs over time</li>
                        <li>• Improved efficiency in repeated tasks</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button 
                    onClick={() => setShowMemoryPopup(false)}
                    className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Connectors Popup */}
        {showConnectorsPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-10"></div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>ChatGPT Connectors</h2>
                  <button 
                    onClick={() => setShowConnectorsPopup(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X size={24} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </button>
                </div>

                <div className={`${darkMode ? 'bg-orange-900/20 border-orange-500' : 'bg-orange-50 border-orange-200'} rounded-lg border p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <Link2 size={20} className={`${darkMode ? 'text-orange-400' : 'text-orange-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>Connect Your Apps</h4>
                      <p className={`text-sm ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                        Connect various apps and services to ChatGPT so it can access your information and provide more personalized assistance. This allows ChatGPT to read from and write to your connected accounts.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Available Apps & Services</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {['Box', 'Canva', 'Dropbox', 'Gmail', 'Google Calendar', 'Google Contacts', 'Google Drive', 'HubSpot', 'Linear', 'Notion', 'Outlook Calendar', 'Outlook Email', 'SharePoint', 'Teams'].map((app) => (
                      <div key={app} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{app[0]}</span>
                          </div>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{app}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4 mt-6`}>
                  <div className="flex items-start gap-3">
                    <Link2 size={20} className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>How to Connect</h4>
                      <ol className={`text-sm space-y-1 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        <li>1. Go to your ChatGPT settings</li>
                        <li>2. Navigate to the "Connections" section</li>
                        <li>3. Click "Add connection" for the app you want to connect</li>
                        <li>4. Follow the authentication process for that app</li>
                        <li>5. Grant the necessary permissions when prompted</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className={`${darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-200'} rounded-lg border p-4 mt-6`}>
                  <div className="flex items-start gap-3">
                    <Check size={20} className={`${darkMode ? 'text-green-400' : 'text-green-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Benefits of Connections</h4>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                        <li>• ChatGPT can access your files, emails, and calendar</li>
                        <li>• Schedule meetings and send emails directly through ChatGPT</li>
                        <li>• Analyze and summarize documents from your connected apps</li>
                        <li>• Create and edit content in your preferred tools</li>
                        <li>• Streamline your workflow with integrated access</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button 
                    onClick={() => setShowConnectorsPopup(false)}
                    className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prompt Popup */}
        {showPromptPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-10"></div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Optimal ChatGPT Prompting Guide</h2>
                  <button 
                    onClick={() => setShowPromptPopup(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X size={24} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </button>
                </div>

                <div className={`${darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-200'} rounded-lg border p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <MessageSquare size={20} className={`${darkMode ? 'text-red-400' : 'text-red-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>Master the Art of Prompting</h4>
                      <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                        Learn how to structure your prompts for optimal ChatGPT outputs. This guide covers both minimum and maximum prompt frameworks to help you get the best results.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 mb-8">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Minimum Framework</h3>
                  
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>1. Context</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Provide background information relevant to the assistant.
                      </p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>2. Task</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <ul className={`text-sm space-y-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Objective:</strong> State the main goal of the task</li>
                        <li><strong>Steps:</strong> List the specific actions required to complete the task</li>
                        <li><strong>Outcome:</strong> Define the expected result or deliverable</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>3. Format</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Outline the structure and presentation of the output.
                      </p>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Templates:</strong> Provide or refer to any templates to be used</li>
                        <li><strong>Style Guide:</strong> Specify formatting</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 mb-8">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Maximum Framework</h3>
                  
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>1. Context</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Purpose:</strong> Explain why the GPT is being developed</li>
                        <li><strong>Audience:</strong> Identify who will use the GPT</li>
                        <li><strong>Scope:</strong> Outline the boundaries and extent of the project</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>2. Task</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Steps:</strong> List the specific actions required</li>
                        <li><strong>Outcome:</strong> Define the expected result</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>3. Rules</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Dos and Don'ts:</strong> Highlight what is allowed and what should be avoided</li>
                        <li><strong>Standards:</strong> Specify any industry or company standards</li>
                        <li><strong>Limitations:</strong> Mention any limitations or restrictions</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>4. Format</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Templates:</strong> Provide or refer to any templates to be used</li>
                        <li><strong>Style Guide:</strong> Specify formatting rules</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>5. Examples</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <p className={`text-sm mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Offer sample outputs or references to illustrate the expected outcome.
                      </p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>6. Tone</h4>
                    <div className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li><strong>Voice:</strong> Describe whether the tone should be technical, formal, collaborative, etc.</li>
                        <li><strong>Audience Consideration:</strong> Tailor the tone to suit the technical expertise</li>
                        <li><strong>Consistency:</strong> Ensure the tone is consistent with company branding</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className={`${darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-200'} rounded-lg border p-4 mb-6`}>
                  <div className="flex items-start gap-3">
                    <Lightbulb size={20} className={`${darkMode ? 'text-green-400' : 'text-green-600'} mt-0.5`} />
                    <div>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Pro Tips</h4>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                        <li>• Start with the minimum framework and expand as needed</li>
                        <li>• Be specific about your desired output format</li>
                        <li>• Include relevant context to help ChatGPT understand your needs</li>
                        <li>• Use examples to illustrate your expectations</li>
                        <li>• Iterate and refine your prompts based on results</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button 
                    onClick={() => setShowPromptPopup(false)}
                    className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Popup */}
        {showFeaturesPopup && selectedFeature && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-10"></div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Feature Details</h2>
                  <button 
                    onClick={() => setShowFeaturesPopup(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X size={24} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                      {selectedFeature.imageUrl ? (
                        <img
                          src={selectedFeature.imageUrl}
                          alt={selectedFeature.title}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <Lightbulb size={32} className="text-orange-500" />
                      )}
                    </div>
                    <div>
                      <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedFeature.title}</h3>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Description</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{selectedFeature.description}</p>
                  </div>

                  {selectedFeature.useCases && selectedFeature.useCases.length > 0 && (
                    <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                      <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Use Cases</h4>
                      <div className="space-y-3">
                        {selectedFeature.useCases.map((useCase, index) => (
                          <div key={index} className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-lg border p-3`}>
                            <h5 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{useCase.title}</h5>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{useCase.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedFeature.details && (
                    <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg border p-4`}>
                      <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Details</h4>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{selectedFeature.details}</p>
                    </div>
                  )}

                  {selectedFeature.benefits && (
                    <div className={`${darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-200'} rounded-lg border p-4`}>
                      <h4 className={`font-semibold mb-3 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Benefits</h4>
                      <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-600'}`}>{selectedFeature.benefits}</p>
                    </div>
                  )}

                  {selectedFeature.instructions && (
                    <div className={`${darkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4`}>
                      <h4 className={`font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>How to Use</h4>
                      <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>{selectedFeature.instructions}</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-6">
                  <button 
                    onClick={() => setShowFeaturesPopup(false)}
                    className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartGPTs;

